/**
 * useCommand hook - Execute commands with optimistic updates
 * UI components should ONLY use this hook for all write operations
 */

import { useCallback } from 'react'
import { localDB } from '../../db/client'
import { serverSyncService } from '../../core/infrastructure/server-sync-service'
import { tabSyncCoordinator } from '../../core/coordination/tab-sync-coordinator'
import { memoryStateService } from '../../core/state/memory-store'
import { getNextOrders } from '../../core/utils/ordering'
import { generateCommandId } from '../../core/commands/utils'
import { getCurrentISOTimestamp } from '../../core/utils/timestamp'
import type { Scenario } from '../../core/domain/models'
import type {
  Command,
  CommandResponse,
  CreateItemCommand,
  CreateItemsCommand,
  UpdateItemQuantityCommand,
  CompleteItemCommand,
  DeleteItemCommand,
  RenameItemCommand,
  ReorderItemCommand,
  CreateListCommand,
  DeleteListCommand,
  RenameListCommand,
  CreateFlowCommand,
  RenameFlowCommand,
  DeleteFlowCommand,
  UpdateFlowRankingsCommand,
  SetDefaultFlowCommand,
  SetItemFlowPreferencesCommand,
  RenameScenarioCommand,
  DeleteScenarioCommand,
  CreateScenarioCommand,
  LeaveScenarioCommand,
  DeleteScenarioItemsCommand,
  AddScenarioItemsCommand
} from '../../core/commands/types'
import { buildCommandPresentation, resolveErrorMessage, type CommandNotificationContext } from '../notifications/command-notifications'
import { toastStore } from '../notifications/toast-store'

export function useCommand() {
  const enqueueAndSync = useCallback(() => {
    // Keep pending count indicator responsive
    serverSyncService.queueChanged()
    // Fire-and-forget; sync loop handles retries/backoff
    void serverSyncService.sync()
  }, [])

  const notificationContext: CommandNotificationContext = {
    getScenario: (scenarioId) => memoryStateService.getScenario(scenarioId),
    getList: (listId) => memoryStateService.getList(listId),
    getItem: (listId, itemId) => memoryStateService.getItem(listId, itemId),
    getItemsByList: (listId) => memoryStateService.getItemsByList(listId),
    getFlow: (flowId) => memoryStateService.getFlow(flowId),
    getScenarioItems: (scenarioId) => memoryStateService.getScenarioItems(scenarioId)
  }

  interface CommandExecutionOptions {
    onUndo?: () => void
    queueCommand?: () => Promise<void>
    skipNotification?: boolean
  }

  const executeCommandWithNotifications = useCallback(async (
    command: Command,
    options?: CommandExecutionOptions
  ): Promise<CommandResponse> => {
    const presentation = buildCommandPresentation(command, notificationContext)
    const queueCommand = options?.queueCommand ?? (() => localDB.addToSyncQueue(command, presentation.uiMetadata))

    try {
      await queueCommand()
    } catch (error) {
      toastStore.push({
        message: resolveErrorMessage(command, notificationContext),
        type: 'error'
      })
      throw error
    }

    const result = await tabSyncCoordinator.executeCommand(command)
    enqueueAndSync()

    if (result.status === 'error') {
      if (!command.isUndo) {
        toastStore.push({
          message: resolveErrorMessage(command, notificationContext, result),
          type: 'error'
        })
      }
      return result
    }

    if (command.isUndo) {
      return result
    }

    // Skip notification if requested (e.g., for composed operations like rollover)
    if (options?.skipNotification) {
      return result
    }

    const { notification } = presentation
    const action = notification.undo
      ? {
          label: notification.undo.label,
          onClick: () => {
            options?.onUndo?.()
            const undoCommands = notification.undo?.buildCommands() ?? []
            void (async () => {
              for (const undoCommand of undoCommands) {
                await executeCommandWithNotifications(undoCommand)
              }
            })()
          }
        }
      : undefined

    toastStore.push({
      message: notification.message,
      itemName: notification.itemName,
      type: notification.type,
      duration: notification.duration,
      action
    })

    return result
  }, [enqueueAndSync])

  /**
   * Create an item with optimistic update
   */
  const createItem = useCallback(async (
    scenarioId: string,
    listId: string,
    itemId: string,
    itemName: string,
    order: number,
    quantity?: number
  ): Promise<CommandResponse> => {
    const command: CreateItemCommand = {
      commandId: generateCommandId(),
      commandType: 'CreateItem',
      scenarioId,
      clientTimestamp: new Date().toISOString(),
      listId,
      itemId,
      itemName,
      order,
      quantity
    }

    return executeCommandWithNotifications(command)
  }, [])

  /**
   * Update item quantity with optimistic update
   */
  const updateItemQuantity = useCallback(async (
    scenarioId: string,
    listId: string,
    itemId: string,
    quantity: number,
    updateMetaItem?: boolean
  ): Promise<CommandResponse> => {
    const command: UpdateItemQuantityCommand = {
      commandId: generateCommandId(),
      commandType: 'UpdateItemQuantity',
      scenarioId,
      clientTimestamp: new Date().toISOString(),
      listId,
      itemId,
      quantity,
      updateMetaItem
    }

    return executeCommandWithNotifications(command)
  }, [])

  /**
   * Create multiple items with optimistic update (single command)
   */
  const createItems = useCallback(async (
    scenarioId: string,
    listId: string,
    items: Array<{ itemId: string; itemName: string; order: number; quantity?: number }>
  ): Promise<CommandResponse> => {
    const command: CreateItemsCommand = {
      commandId: generateCommandId(),
      commandType: 'CreateItems',
      scenarioId,
      clientTimestamp: new Date().toISOString(),
      listId,
      items
    }

    return executeCommandWithNotifications(command)
  }, [])

  /**
   * Complete/uncomplete an item with optimistic update
   */
  const completeItem = useCallback(async (
    scenarioId: string,
    listId: string,
    itemId: string,
    isComplete: boolean,
    _itemName?: string,
    options?: CommandExecutionOptions
  ): Promise<CommandResponse> => {
    const command: CompleteItemCommand = {
      commandId: generateCommandId(),
      commandType: 'CompleteItem',
      scenarioId,
      clientTimestamp: new Date().toISOString(),
      listId,
      itemId,
      isComplete
    }

    return executeCommandWithNotifications(command, options)
  }, [])

  /**
   * Delete an item with optimistic update
   */
  const deleteItem = useCallback(async (
    scenarioId: string,
    listId: string,
    itemId: string,
    _itemName?: string,
    options?: CommandExecutionOptions
  ): Promise<CommandResponse> => {
    const command: DeleteItemCommand = {
      commandId: generateCommandId(),
      commandType: 'DeleteItem',
      scenarioId,
      clientTimestamp: new Date().toISOString(),
      listId,
      itemId
    }

    return executeCommandWithNotifications(command, options)
  }, [])

  /**
   * Rename an item with optimistic update
   */
  const renameItem = useCallback(async (
    scenarioId: string,
    listId: string,
    itemId: string,
    newName: string,
    _oldName?: string
  ): Promise<CommandResponse> => {
    const command: RenameItemCommand = {
      commandId: generateCommandId(),
      commandType: 'RenameItem',
      scenarioId,
      clientTimestamp: new Date().toISOString(),
      listId,
      itemId,
      newName
    }

    return executeCommandWithNotifications(command)
  }, [])

  /**
   * Reorder an item by updating its sort order (fractional indexing)
   */
  const reorderItem = useCallback(async (
    scenarioId: string,
    listId: string,
    itemId: string,
    newOrder: number,
    _itemName?: string,
    options?: CommandExecutionOptions
  ): Promise<CommandResponse> => {
    const command: ReorderItemCommand = {
      commandId: generateCommandId(),
      commandType: 'ReorderItem',
      scenarioId,
      clientTimestamp: new Date().toISOString(),
      listId,
      itemId,
      newOrder
    }

    return executeCommandWithNotifications(command, options)
  }, [])

  /**
   * Create a list with optimistic update
   */
  const createList = useCallback(async (
    scenarioId: string,
    listId: string,
    listName: string,
    description?: string
  ): Promise<CommandResponse> => {
    const command: CreateListCommand = {
      commandId: generateCommandId(),
      commandType: 'CreateList',
      scenarioId,
      clientTimestamp: getCurrentISOTimestamp(),
      listId,
      listName,
      description
    }

    return executeCommandWithNotifications(command)
  }, [])

  /**
   * Delete a list with optimistic update
   */
  const deleteList = useCallback(async (
    scenarioId: string,
    listId: string,
    _listName?: string
  ): Promise<CommandResponse> => {
    const command: DeleteListCommand = {
      commandId: generateCommandId(),
      commandType: 'DeleteList',
      scenarioId,
      clientTimestamp: new Date().toISOString(),
      listId
    }

    return executeCommandWithNotifications(command)
  }, [])

  /**
   * Rename a list with optimistic update
   */
  const renameList = useCallback(async (
    scenarioId: string,
    listId: string,
    newName: string,
    newDescription?: string,
    _oldName?: string
  ): Promise<CommandResponse> => {
    const command: RenameListCommand = {
      commandId: generateCommandId(),
      commandType: 'RenameList',
      scenarioId,
      clientTimestamp: new Date().toISOString(),
      listId,
      newName,
      newDescription
    }

    return executeCommandWithNotifications(command)
  }, [])

  /**
   * Create a flow with optimistic update
   */
  const createFlow = useCallback(async (
    scenarioId: string,
    flowId: string,
    flowName: string,
    description?: string,
    color?: string
  ): Promise<CommandResponse> => {
    const command: CreateFlowCommand = {
      commandId: generateCommandId(),
      commandType: 'CreateFlow',
      scenarioId,
      clientTimestamp: getCurrentISOTimestamp(),
      flowId,
      flowName,
      description,
      color
    }

    return executeCommandWithNotifications(command)
  }, [])

  /**
   * Delete a flow with optimistic update
   */
  const deleteFlow = useCallback(async (
    scenarioId: string,
    flowId: string,
    _flowName?: string
  ): Promise<CommandResponse> => {
    const command: DeleteFlowCommand = {
      commandId: generateCommandId(),
      commandType: 'DeleteFlow',
      scenarioId,
      clientTimestamp: new Date().toISOString(),
      flowId
    }

    return executeCommandWithNotifications(command)
  }, [])

  /**
   * Rename a flow with optimistic update
   */
  const renameFlow = useCallback(async (
    scenarioId: string,
    flowId: string,
    newName: string,
    newDescription?: string,
    newColor?: string,
    _oldName?: string
  ): Promise<CommandResponse> => {
    const command: RenameFlowCommand = {
      commandId: generateCommandId(),
      commandType: 'RenameFlow',
      scenarioId,
      clientTimestamp: new Date().toISOString(),
      flowId,
      newName,
      newDescription,
      newColor
    }

    return executeCommandWithNotifications(command)
  }, [])

  /**
   * Update flow rankings (batch reorder multiple items)
   */
  const updateFlowRankings = useCallback(async (
    scenarioId: string,
    flowId: string,
    rankings: Record<string, { r: number; n?: string; notAvailable: boolean }>
  ): Promise<CommandResponse> => {
    const command: UpdateFlowRankingsCommand = {
      commandId: generateCommandId(),
      commandType: 'UpdateFlowRankings',
      scenarioId,
      clientTimestamp: new Date().toISOString(),
      flowId,
      rankings
    }

    return executeCommandWithNotifications(command)
  }, [])

  /**
   * Set item flow preferences (status across multiple flows for a single item)
   */
  const setItemFlowPreferences = useCallback(async (
    scenarioId: string,
    itemId: string,
    flowPreferences: Record<string, { status: 'normal' | 'preferred' | 'notAvailable' }>,
    options?: { skipNotification?: boolean }
  ): Promise<CommandResponse> => {
    const command: SetItemFlowPreferencesCommand = {
      commandId: generateCommandId(),
      commandType: 'SetItemFlowPreferences',
      scenarioId,
      clientTimestamp: new Date().toISOString(),
      itemId,
      flowPreferences
    }

    return executeCommandWithNotifications(command, options)
  }, [])

  /**
   * Set a flow as the default flow for a scenario
   */
  const setDefaultFlow = useCallback(async (
    scenarioId: string,
    flowId: string
  ): Promise<CommandResponse> => {
    const command: SetDefaultFlowCommand = {
      commandId: generateCommandId(),
      commandType: 'SetDefaultFlow',
      scenarioId,
      clientTimestamp: new Date().toISOString(),
      flowId
    }

    return executeCommandWithNotifications(command)
  }, [])

  /**
   * Rename a scenario with optimistic update
   */
  const renameScenario = useCallback(async (
    scenarioId: string,
    newName: string,
    newDescription?: string,
    _oldName?: string
  ): Promise<CommandResponse> => {
    const command: RenameScenarioCommand = {
      commandId: generateCommandId(),
      commandType: 'RenameScenario',
      scenarioId,
      clientTimestamp: new Date().toISOString(),
      newName,
      newDescription
    }

    return executeCommandWithNotifications(command)
  }, [])

  /**
   * Delete a scenario with optimistic update
   */
  const deleteScenario = useCallback(async (
    scenarioId: string,
    _scenarioName?: string
  ): Promise<CommandResponse> => {
    const command: DeleteScenarioCommand = {
      commandId: generateCommandId(),
      commandType: 'DeleteScenario',
      scenarioId,
      clientTimestamp: new Date().toISOString()
    }

    return executeCommandWithNotifications(command)
  }, [])

  const createScenario = useCallback(async (
    scenarioId: string,
    name: string,
    description: string
  ): Promise<CommandResponse> => {
    const command: CreateScenarioCommand = {
      commandId: generateCommandId(),
      commandType: 'CreateScenario',
      scenarioId,
      clientTimestamp: getCurrentISOTimestamp(),
      name,
      description
    }

    // CreateScenario is special: Save to IndexedDB immediately (sequenceNumber=0 placeholder)
    // This allows the scenario to be used before the server responds
    const newScenario: Scenario = {
      id: scenarioId,
      name,
      description: description || '',
      // ownerId: '', // Removed in favor of UserAccess role system
      role: 'owner',
      sequenceNumber: 0,
      isDeleted: false,
      createdAt: getCurrentISOTimestamp(),
      updatedAt: getCurrentISOTimestamp()
    }

    // Atomically save scenario and queue command (transaction ensures both or neither)
    const queueCommand = () => localDB.saveScenarioAndQueue(newScenario, command)

    return executeCommandWithNotifications(command, { queueCommand })
  }, [])

  const deleteScenarioItems = useCallback(async (
    scenarioId: string,
    itemIds: string[]
  ): Promise<CommandResponse> => {
    const command: DeleteScenarioItemsCommand = {
        commandId: generateCommandId(),
        commandType: 'DeleteScenarioItems',
        scenarioId,
        clientTimestamp: new Date().toISOString(),
        itemIds
    }

    return executeCommandWithNotifications(command)
  }, [])

  const addScenarioItems = useCallback(async (
    scenarioId: string,
    itemNames: string[]
  ): Promise<CommandResponse> => {
    const command: AddScenarioItemsCommand = {
        commandId: generateCommandId(),
        commandType: 'AddScenarioItems',
        scenarioId,
        clientTimestamp: new Date().toISOString(),
        itemNames
    }

    return executeCommandWithNotifications(command)
  }, [])

  /**
   * Helper to add multiple items to a list.
   * Adds items to a list with proper ordering.
   * Receives items with their scenario item IDs to ensure consistency across lists and flows.
   */
  const addItems = useCallback(async (
    scenarioId: string,
    listId: string,
    itemsToAdd: Array<{ id: string; name: string; quantity?: number }>
  ): Promise<void> => {
    const validItems = itemsToAdd.filter(item => item.id && item.name.trim())
    if (validItems.length === 0) return

    const scenarioItems = memoryStateService.getScenarioItems(scenarioId)
    const quantityByItemId = new Map(scenarioItems.map(i => [i.id, i.quantity]))

    // Get the list from memory state to determine the starting order
    const existingItems = memoryStateService.getItemsByList(listId)
    
    // Use centralized ordering logic with proper gaps for reordering
    const orders = getNextOrders(existingItems, validItems.length)

    // Create all items with the provided IDs (reusing scenario item IDs)
    const itemsForCommand = validItems.map((item, i) => ({
      itemId: item.id,
      itemName: item.name.trim(),
      order: orders[i],
      quantity: item.quantity ?? quantityByItemId.get(item.id)
    }))
    
    await createItems(scenarioId, listId, itemsForCommand)
  }, [createItems])

  const leaveScenario = useCallback(async (
    scenarioId: string
  ): Promise<CommandResponse> => {
    const command: LeaveScenarioCommand = {
      commandId: generateCommandId(),
      commandType: 'LeaveScenario',
      scenarioId,
      clientTimestamp: new Date().toISOString()
    }

    return executeCommandWithNotifications(command)
  }, [])

  /**
   * Roll over a list: create new list with incomplete items, mark old items as complete
   * 
   * This is a composed operation that executes multiple commands:
   * 1. CreateList - new list with given name
   * 2. CreateItems - copy incomplete items to new list
   * 3. CompleteItem × N - mark old incomplete items as complete
   * 
   * Shows single notification with no undo (would be complex to undo 3+ commands)
   */
  const rollOverList = useCallback(async (
    scenarioId: string,
    sourceListId: string,
    newListName: string
  ): Promise<{ newListId: string }> => {
    // Get incomplete items from source list
    const sourceItems = memoryStateService.getItemsByList(sourceListId)
    const incompleteItems = sourceItems.filter(item => !item.isCompleted)

    // Generate new list ID
    const newListId = `list_${crypto.randomUUID()}`

    try {
      // 1. Create new list (suppress individual notification)
      await executeCommandWithNotifications(
        {
          commandId: generateCommandId(),
          commandType: 'CreateList',
          scenarioId,
          clientTimestamp: getCurrentISOTimestamp(),
          listId: newListId,
          listName: newListName
        },
        { skipNotification: true }
      )

      // 2. Copy incomplete items to new list (suppress individual notification)
      if (incompleteItems.length > 0) {
        // Sort by order to preserve ordering
        const sortedItems = [...incompleteItems].sort((a, b) => a.order - b.order)
        
        // Get next orders for new list
        const orders = getNextOrders([], sortedItems.length)
        
        const itemsForCommand = sortedItems.map((item, index) => ({
          itemId: `item_${crypto.randomUUID()}`,
          itemName: item.name,
          order: orders[index]
        }))

        await executeCommandWithNotifications(
          {
            commandId: generateCommandId(),
            commandType: 'CreateItems',
            scenarioId,
            clientTimestamp: getCurrentISOTimestamp(),
            listId: newListId,
            items: itemsForCommand
          },
          { skipNotification: true }
        )

        // 3. Mark all incomplete items in old list as complete (suppress individual notifications)
        for (const item of incompleteItems) {
          await executeCommandWithNotifications(
            {
              commandId: generateCommandId(),
              commandType: 'CompleteItem',
              scenarioId,
              clientTimestamp: getCurrentISOTimestamp(),
              listId: sourceListId,
              itemId: item.id,
              isComplete: true
            },
            { skipNotification: true }
          )
        }
      }

      // Show single summary notification (no undo - too complex for multi-command operation)
      toastStore.push({
        message: `List "${newListName}" created`,
        type: 'success'
      })

      return { newListId }
    } catch (error) {
      // If anything fails, show error
      toastStore.push({
        message: 'Failed to roll over list',
        type: 'error'
      })
      throw error
    }
  }, [enqueueAndSync])

  return {
    createItem,
    createItems,
    updateItemQuantity,
    completeItem,
    deleteItem,
    renameItem,
    reorderItem,
    createList,
    deleteList,
    renameList,
    renameScenario,
    deleteScenario,
    leaveScenario,
    createScenario,
    deleteScenarioItems,
    addScenarioItems,
    addItems,
    createFlow,
    deleteFlow,
    renameFlow,
    updateFlowRankings,
    setItemFlowPreferences,
    rollOverList,
    setDefaultFlow
  }
}

